import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from 'common/components/Icon';
import { SecondaryButton } from 'common/components/Form/SecondaryButton';
import { useCopyToClipboard } from 'common/hooks/useCopyToClipboard';

interface Props {
  value: string;
  /** Called after every attempt, e.g. to track or to unlock the next step. */
  onCopy?: (copied: boolean) => void;
  /** The small 32px button (default) or the full 40px one, to sit next to a text field. */
  compact?: boolean;
  'data-testid'?: string;
}

const CopyButton: React.FC<Props> = ({ value, onCopy, compact = true, 'data-testid': dataTestId }) => {
  const { t } = useTranslation();
  const { copy, status } = useCopyToClipboard();

  const handleClick = async () => {
    const copied = await copy(value);
    onCopy?.(copied);
  };

  const labels = {
    idle: t('common.copy'),
    copied: t('common.copied'),
    failed: t('common.copyFailed'),
  };

  return (
    <SecondaryButton
      type='button'
      color='white'
      compact={compact}
      isButtonV2
      onClick={handleClick}
      iconOnly
      aria-live='polite'
      aria-label={labels[status]}
      title={labels[status]}
      data-testid={dataTestId}
    >
      <Icon
        icon={status === 'copied' ? 'check' : 'copy-action'}
        size={compact ? 16 : 18}
        color={status === 'copied' ? 'green700' : 'gray700'}
      />
    </SecondaryButton>
  );
};

export default React.memo(CopyButton);
